import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import axios, { AxiosResponse } from "axios";
import { getAuthHeader } from "@micad/vue-components";
import config from "@/config";
import router, { anonymousRoutes } from "@/router";

export interface ApiResponse<T> {
  ok: boolean;
  payload?: T;
  status?: {
    errorMessage?: string;
  };
  response?: AxiosResponse;
}

export const httpClient = axios.create({
  headers: {},
});

httpClient.interceptors.request.use(
  (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    request.baseURL = config.BASE_API_URL;
    const bearerToken = getAuthHeader();
    if (bearerToken && !bearerToken.endsWith("undefined")) {
      request.headers.set("Authorization", bearerToken);
    }
    return request;
  }
);

httpClient.interceptors.response.use(
  (response: AxiosResponse<unknown>): AxiosResponse<unknown> => response,
  (err: AxiosError) => {
    if (err?.response) {
      if (err.response.status === 401) {
        const requestUrl = err.config?.url || "";
        // do not logout if savedSession returns 401 to avoid redirect loop
        if (
          requestUrl.search("/api/user/savedSession$") > -1 ||
          requestUrl.search("/api/user/login$") > -1 ||
          requestUrl.search("/api/user/logout$") > -1 ||
          requestUrl.search("/api/client/config") > -1 ||
          requestUrl.search("/api/logUserActivity") > -1 ||
          anonymousRoutes.includes(String(router.currentRoute.name))
        ) {
          return Promise.reject(err);
        }
        const url = new URL(window.location.href);
        const returnUrl = url.searchParams.get("returnUrl");
        const fallbackReturnUrl = encodeURIComponent(window.location.origin);
        if (router.currentRoute.name !== "pre-login")
          window.location.href = `${
            window.location.origin
          }/pre-login?returnUrl=${returnUrl || fallbackReturnUrl}`;
      } else if (err.response.status > 499 && err.response.status < 600) {
        console.error("request failed", err.response);
      }
    }
    return Promise.reject(err);
  }
);

export async function postRequest(
  url: string,
  data: unknown
): Promise<ApiResponse<any>> {
  try {
    const response = await httpClient.post(url, data);
    if (response?.status < 400)
      return {
        ok: !response.data.errorMessage,
        payload: response.data,
      };
    else
      return {
        ok: false,
        status: response?.data,
        response,
      };
  } catch (err) {
    console.log("postRequest err:", err);
    return { ok: false, response: (err as AxiosError).response };
  }
}

export async function putRequest(
  url: string,
  data: unknown
): Promise<ApiResponse<any>> {
  try {
    const response = await httpClient.put(url, data);
    if (response?.status < 400)
      return {
        ok: !response.data.errorMessage,
        payload: response.data,
      };
    else
      return {
        ok: false,
        status: response?.data,
        response,
      };
  } catch (err) {
    console.log("putRequest err:", err);
    return { ok: false, response: (err as AxiosError).response };
  }
}

export async function deleteRequest(
  url: string,
  data: unknown
): Promise<ApiResponse<any>> {
  try {
    const response = await httpClient.delete(url, { data: data });
    if (response?.status < 400)
      return {
        ok: !response.data.errorMessage,
        payload: response.data,
      };
    else
      return {
        ok: false,
        status: response?.data,
        response,
      };
  } catch (err) {
    console.log("putRequest err:", err);
    return { ok: false, response: (err as AxiosError).response };
  }
}

export async function getRequest(
  url: string,
  params: unknown = {}
): Promise<ApiResponse<any>> {
  try {
    const response = await httpClient.get(url, {
      params,
      headers: { Authorization: getAuthHeader() },
    });

    if (response.status < 400)
      return {
        ok: !response.data.errorMessage,
        payload: response.data,
      };
    else
      return {
        ok: false,
        status: response.data,
        response,
      };
  } catch (err) {
    return { ok: false, response: (err as AxiosError).response };
  }
}
